import classNames from "classnames";
import * as React from "react";
import { KModalFooter, type IKModalFooterProps } from "../../../ui-kit/k-modal/k-modal-footer";
import KuikaInfoBox from "../../../ui-kit/kuika-info-box/kuika-info-box";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import "./config-modal-parts.scss";

/**
 * The shape every settings dialog is built from.
 *
 * Sixty-seven of them open behind an ADD NEW row — one per kind of saved setting — and
 * each spelled the same dialog out inline: an antd `Row`/`Col` wrapping a padded div, then
 * a footer div with `borderTop: "1px solid #e1e5eb"` and two antd Buttons carrying
 * `k_button` / `k_button-delete`. The hardcoded rule is why the dialogs kept a light
 * hairline across their footer on the dark theme, and the inline `padding: 24` is why
 * adjusting the dialogs meant touching sixty-seven files.
 *
 * `DefaultModal` owns the frame; these own what is inside it.
 */

export interface IConfigModalBodyProps {
  children: React.ReactNode;
  /** The dialog's own copy, above the fields. */
  description?: React.ReactNode;
  className?: string;
}

export interface IConfigModalNoteProps {
  children: React.ReactNode;
  /**
   * `note` is the dialog's own explanatory copy. `info` and `warning` are what antd's
   * `Alert` drew — a condition of the value being entered, not a description of the dialog.
   */
  tone?: "note" | "info" | "warning";
  className?: string;
}

// `note` and `info` share the blue: a grey box on a grey dialog is a rectangle you have to
// look for, and the tint is what makes the copy read as an aside rather than as the first
// field. `--blue-1` is a deep navy on the dark theme and a pale wash on the light one, so
// it tints without lifting off the surface. `warning` keeps its own colour — that one has
// to be told apart at a glance.
const NOTE_COLOUR: Record<string, "gray" | "blue" | "yellow"> = {
  note: "blue",
  info: "blue",
  warning: "yellow"
};

/**
 * A dialog's explanatory copy.
 *
 * It was a `property__topic-text` paragraph in seventy files, styled from `styling.scss` at
 * a size that made it the loudest thing in the dialog. In a box it reads as a note about
 * the dialog rather than as its first field, and the (i) says which it is without a word.
 */
export const ConfigModalNote = ({ children, tone = "note", className }: IConfigModalNoteProps) => {
  // `getSectionDescription` returns `undefined` for a key with no localization, and the
  // workspace surface loads none at all — an empty box with a lone (i) in it is worse than
  // no box.
  if (children === undefined || children === null || children === false || children === "") return null;

  return (
    <KuikaInfoBox
      className="kuika-config-modal__note"
      color={NOTE_COLOUR[tone]}
      leftUnit={
        <span
          className={classNames(
            "kuika-config-modal__description",
            `kuika-config-modal__description--${tone}`,
            className
          )}
        >
          <KuikaSvg
            name={tone === "warning" ? "alert-triangle" : "info-circle"}
            width={16}
            height={16}
            className="kuika-config-modal__description-icon"
          />
          <KuikaSmallText>{children}</KuikaSmallText>
        </span>
      }
    />
  );
};

export interface IConfigModalGroupHeadingProps {
  children: React.ReactNode;
  /**
   * For a heading that is the label of something else — a collapse header, say. It drops
   * the vertical rhythm and the rule, which that host draws itself; left on, the margins
   * push the text off the header's own midline.
   */
  inline?: boolean;
}

/**
 * A run of fields inside a dialog — "TRANSACTION/SPAN SETTINGS", "PERFORMANCE SETTINGS".
 *
 * It is not a `ConfigSection`: those are the rail's categories, and a dialog has no rail.
 * The three that exist were `topic-title` divs carrying `fontSize: 11, color: "#818C99"`
 * inline.
 */
export const ConfigModalGroupHeading = ({ children, inline }: IConfigModalGroupHeadingProps) => (
  <div
    className={classNames("kuika-config-modal__group-heading", {
      "kuika-config-modal__group-heading--inline": inline
    })}
  >
    {children}
  </div>
);

export const ConfigModalBody = ({ children, description, className }: IConfigModalBodyProps) => (
  <div className={classNames("kuika-config-modal__body", className)}>
    {/* Above the fields, not below them: it says what the dialog is for, which is worth
        knowing before filling it in rather than after. */}
    {description && <ConfigModalNote>{description}</ConfigModalNote>}
    {children}
  </div>
);

export interface IConfigModalFooterProps {
  /** Shown only while editing an existing setting — there is nothing to delete otherwise. */
  onDelete?: () => void;
  deleteTestId?: string;
  deleteLabel?: string;
  submitTestId?: string;
  /** "Create" while making one, "Update" once it exists. Cased by the footer, not here. */
  submitLabel: string;
  /**
   * The `id` of the dialog's `<Form>`. The footer sits in `KModal`'s own slot now, which is
   * a sibling of the body rather than a descendant, so nesting can no longer associate the
   * two — the HTML `form` attribute does it instead.
   */
  submitForm?: string;
  loading?: boolean;
  /**
   * The stateful commit: the button rolls to "Saving", then "Saved" or "Try again", and its
   * width follows. A dialog that has not been converted passes `loading` instead and keeps
   * today's spinner — `status` wins for the commit when both are given.
   */
  status?: IKModalFooterProps["status"];
  statusLabels?: IKModalFooterProps["statusLabels"];
  /** Where Delete stands, on Delete's own button — see `KModalFooter`. */
  deleteStatus?: IKModalFooterProps["deleteStatus"];
  /** Extra controls beside Delete, for the dialogs that have one. */
  children?: React.ReactNode;
}

/**
 * The settings dialogs' footer: `KModalFooter` with the one thing that is theirs — Delete
 * appears only when there is a saved setting to delete.
 *
 * It has no Cancel. These dialogs are reached from an ADD NEW row and left by the close
 * control, and adding a second way out to sixty-seven of them is not this change.
 */
export const ConfigModalFooter = ({
  onDelete,
  deleteTestId,
  deleteLabel,
  submitTestId,
  submitLabel,
  submitForm,
  loading,
  status,
  statusLabels,
  deleteStatus,
  children
}: IConfigModalFooterProps) => (
  <KModalFooter
    onDelete={onDelete}
    deleteTestId={deleteTestId}
    deleteLabel={deleteLabel}
    submitTestId={submitTestId}
    submitLabel={submitLabel}
    submitForm={submitForm}
    loading={loading}
    status={status}
    statusLabels={statusLabels}
    deleteStatus={deleteStatus}
  >
    {children}
  </KModalFooter>
);
